import { User as OIDCUser, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import create from 'zustand';
import { BackstageConfig } from '../backstage/Backstage';
import {
  applyBackstageConfigurationOverride,
  getRuntimeBackstageSettings,
  getVariable
} from '../backstage/BackstageProvider';
import { useAdminLocationStore } from '../location/useAdminLocationStore';
import {
  createDelegationDebugTools,
  createDelegationRenewalController,
  endDelegatedSessionRequest,
  loadCurrentDelegatedSession,
  renewDelegatedSessionRequest,
  startDelegatedSessionRequest,
  toUserDelegation
} from './authentication-delegation';
import { AuthenticationState, Role } from './authentication.types';

let host = window.location.protocol.concat('//');
host = host.concat(window.location.hostname);
if (window.location.port) {
  host = host.concat(':');
  host = host.concat(window.location.port);
}

const getConfig = async () => {
  const IDENTITY_CLIENT_ID = await getVariable('authIdentityClientId');
  const AUTH_SERVER_URL = await getVariable('authUrl');
  const REDIRECT_URL = await getVariable('authRedirectUrl');
  const LOGOFF_REDIRECT_URL = await getVariable('authLogoffRedirectUrl');

  const IDENTITY_CONFIG = {
    authority: AUTH_SERVER_URL,
    client_id: IDENTITY_CLIENT_ID,
    redirect_uri: host.concat(REDIRECT_URL),
    post_logout_redirect_uri: host.concat(LOGOFF_REDIRECT_URL),
    response_type: 'code',
    scope: 'openid profile email api.read offline_access IdentityServerApi',
    filterProtocolClaims: true,
    loadUserInfo: true,
    extraQueryParams: {
      setPasswordRedirect: host
    }
  };

  const METADATA_OIDC = {
    issuer: AUTH_SERVER_URL,
    jwks_uri: AUTH_SERVER_URL.concat('/.well-known/openid-configuration/jwks'),
    authorization_endpoint: AUTH_SERVER_URL.concat('/connect/authorize'),
    token_endpoint: AUTH_SERVER_URL.concat('/connect/token'),
    userinfo_endpoint: AUTH_SERVER_URL.concat('/connect/userinfo'),
    end_session_endpoint: AUTH_SERVER_URL.concat('/connect/endsession'),

    /* TODO There is a huge reload loop issue with using this functionality on cookie
         protected browsers (safari, firefox) and modes (chrome incognito)
         check_session_iframe: AUTH_SERVER_URL.concat('/connect/checksession'),
         */
    revocation_endpoint: AUTH_SERVER_URL.concat('/connect/revocation'),
    introspection_endpoint: AUTH_SERVER_URL.concat('/connect/introspect')
  };

  return { IDENTITY_CONFIG, METADATA_OIDC, AUTH_SERVER_URL, IDENTITY_CLIENT_ID };
};

export const isStaff = (role?: Role) => role === Role.Staff;

export const isAdmin = (role?: Role) => role === Role.Admin;

export const isSuperAdmin = (role?: Role) => role === Role.SuperAdmin;

export const isAdminOrSuper = (role?: Role) => isAdmin(role) || isSuperAdmin(role);

export const isNotAdminOrSuper = (role?: Role) => !isAdminOrSuper(role);

export const isManagement = (role?: Role) => isAdminOrSuper(role) || role === Role.Manager;

export enum Permissions {
  EmployeeCreate = 'Employee.Create',
  EmployeeRead = 'Employee.Read',
  EmployeeReadAll = 'Employee.ReadAll',
  EmployeeUpdate = 'Employee.Update',
  EmployeeDelete = 'Employee.Delete',
  EmployeeDeactivate = 'Employee.Deactivate',
  SettingsCreate = 'Settings.Create',
  TemplateCreate = 'Template.Create',
  TemplateRead = 'Template.Read',
  TemplateUpdate = 'Template.Update',
  TemplateDelete = 'Template.Delete',
  WorkingPatternRead = 'WorkingPattern.Read',
  WorkingPatternUpdate = 'WorkingPattern.Update',
  WorkingPatternCreate = 'WorkingPattern.Create',
  AvailabilityCreate = 'Availability.Create',
  AvailabilityRead = 'Availability.Read',
  AvailabilityUpdate = 'Availability.Update',
  OperatingHoursCreate = 'OperatingHours.Create',
  OperatingHoursRead = 'OperatingHours.Read',
  OperatingHoursUpdate = 'OperatingHours.Update',
  OperatingHoursDelete = 'OperatingHours.Delete',
  OptimiserExecute = 'Optimiser.Execute',
  LocationCreate = 'Location.Create',
  LocationRead = 'Location.Read',
  LocationUpdate = 'Location.Update',
  RosterCreate = 'Roster.Create',
  RosterRead = 'Roster.Read',
  RosterUpdate = 'Roster.Update',
  RosterDelete = 'Roster.Delete',
  SettingsRead = 'Settings.Read',
  SettingsUpdate = 'Settings.Update',
  SettingsDelete = 'Settings.Delete',
  SkillsCreate = 'Skills.Create',
  SkillsRead = 'Skills.Read',
  SkillsUpdate = 'Skills.Update',
  SkillsDelete = 'Skills.Delete',
  StationCreate = 'Station.Create',
  StationRead = 'Station.Read',
  StationUpdate = 'Station.Update',
  StationDelete = 'Station.Delete',
  TargetCreate = 'Target.Create',
  TargetUpdate = 'Target.Update',
  TargetDelete = 'Target.Delete',
  TargetRead = 'Target.Read',
  TaskCreate = 'Task.Create',
  TaskRead = 'Task.Read',
  TaskUpdate = 'Task.Update',
  TaskDelete = 'Task.Delete',
  WorkingPatternDelete = 'WorkingPattern.Delete',
  AvailabilityDelete = 'Availability.Delete',
  LocationDelete = 'Location.Delete',
  WorkingTimeDataCreate = 'WorkingTimeData.Create',
  WorkingTimeDataRead = 'WorkingTimeData.Read',
  WorkingTimeDataReadAll = 'WorkingTimeData.ReadAll',
  WorkingTimeDataUpdate = 'WorkingTimeData.Update',
  WorkingTimeDataCreateSelf = 'WorkingTimeData.CreateSelf',
  WorkingTimeDataReadSelf = 'WorkingTimeData.ReadSelf',
  WorkingTimeDataUpdateSelf = 'WorkingTimeData.UpdateSelf'
}

const TENANT_VARIABLE_OVERRIDE_KEYS = new Set(['name', 'logo', 'logoUrl', 'backgroundImageUrl', 'hue']);

const sanitiseTenantConfiguration = (config: unknown): BackstageConfig => {
  if (!config || typeof config !== 'object') {
    return {};
  }

  const tenantConfiguration = config as {
    variables?: Record<string, unknown>;
    flags?: Record<string, unknown>;
  };

  const variables = Object.fromEntries(
    Object.entries(tenantConfiguration.variables ?? {}).filter(
      ([key, value]) => TENANT_VARIABLE_OVERRIDE_KEYS.has(key) && typeof value === 'string'
    )
  ) as Record<string, string>;

  const flags = Object.fromEntries(
    Object.entries(tenantConfiguration.flags ?? {}).filter(([, value]) => typeof value === 'boolean')
  ) as Record<string, boolean>;

  return {
    ...(Object.keys(variables).length > 0 ? { variables } : {}),
    ...(Object.keys(flags).length > 0 ? { flags } : {})
  };
};

export const getTenantConfiguration = async (id: string) => {
  try {
    const backstageSettings = getRuntimeBackstageSettings();
    if (!backstageSettings.url || !backstageSettings.environment) {
      return {};
    }

    const url = `${backstageSettings.url}${backstageSettings.environment}/${id}.json`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch tenant configuration from ${url} (HTTP ${response.status})`);
    }

    const tenantConfiguration = sanitiseTenantConfiguration(await response.json());
    applyBackstageConfigurationOverride(tenantConfiguration);
    return tenantConfiguration;
  } catch (e) {
    return {};
  }
};

export type User = OIDCUser & {
  profile?: {
    organisation?: string;
    permission: Permissions[];
  };
  delegation?: {
    isDelegating: boolean;
    delegatedTenantName?: string;
    delegatedTenantId?: string;
    delegatedRole?: Role;
  };
};

export interface DelegatedSession {
  delegationSessionId: string;
  targetTenantId: string;
  targetTenantName: string;
  effectiveRole: Role;
  actorRole?: Role;
  expiresAt: string;
}

export interface AuthService {
  state: AuthenticationState;
  user?: User;
  delegatedSession?: DelegatedSession;
  originalRole?: Role; /** The user's original role as provided by OIDC (before delegation) */
  role?: Role; /** The effective role used by the application, taking delegation into account */
  name?: string;
  isDelegatedSuperAdmin: boolean; /** SuperAdmin logged in, currently acting as Admin via delegation */
  userManager: Promise<UserManager>;

  setUser: (user: User) => Promise<void>;
  clearUser: () => Promise<void>;
  restore: () => Promise<void>;

  startDelegation: (targetTenantId: string, targetTenantName: string, delegatedRole: Role) => Promise<void>;
  endDelegation: () => Promise<void>;
  getEffectiveTenantId: () =>
    | string
    | undefined; /** Effective tenant id (delegated tenant if delegating, otherwise own tenant) */
}

export const selectDelegatedRole = (state: AuthService) => state.user?.delegation?.delegatedRole;

export const selectIsDelegatedManager = (state: AuthService) =>
  state.originalRole === Role.Admin && selectDelegatedRole(state) === Role.Manager;

export const selectRequiresActingLocation = (state: AuthService) => state.originalRole === Role.Admin;

const ROLE_KEY = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const DELEGATION_RENEW_LEAD_MS = 5 * 60 * 1000;
const delegationDebug = createDelegationDebugTools(import.meta.env.DEV);

const shouldUseDelegatedTenantId = (delegatedSession?: DelegatedSession, originalRole?: Role) =>
  delegatedSession?.effectiveRole === Role.Admin && originalRole === Role.SuperAdmin;

const isManagerDelegationSession = (delegatedSession?: DelegatedSession, originalRole?: Role) =>
  originalRole === Role.Admin && delegatedSession?.effectiveRole === Role.Manager;

const syncDelegationScopedLocation = (delegatedSession?: DelegatedSession, originalRole?: Role) => {
  if (!isManagerDelegationSession(delegatedSession, originalRole)) {
    useAdminLocationStore.getState().setActingLocation(undefined);
  }
};

const getDelegationDebugState = () => {
  const state = useAuthenticationService.getState();
  return {
    originalRole: state.originalRole,
    effectiveRole: state.role,
    delegation: state.user?.delegation,
    delegatedSession: state.delegatedSession
  };
};

const delegationRenewalController = createDelegationRenewalController({
  renewLeadMs: DELEGATION_RENEW_LEAD_MS,
  getExpiresAt: () => useAuthenticationService.getState().delegatedSession?.expiresAt,
  onRenew: async () => {
    await renewDelegatedSession();
  },
  onClear: () => {
    clearDelegationState();
  }
});

export const useAuthenticationService = create<AuthService>((set, get) => ({
  isDelegatedSuperAdmin: false,
  state: AuthenticationState.Restoring,
  restore: async () => {
    const { AUTH_SERVER_URL, IDENTITY_CLIENT_ID } = await getConfig();
    const token = sessionStorage.getItem(`oidc.user:${AUTH_SERVER_URL}:${IDENTITY_CLIENT_ID}`);

    if (!token) {
      set(() => ({ state: AuthenticationState.Unauthenticated }));
      return;
    }

    const user = JSON.parse(token) as User;
    if (user.profile?.organisation) await getTenantConfiguration(user.profile.organisation);

    const oidcRole = user.profile?.[ROLE_KEY] as Role | undefined;
    const delegatedSession = await loadCurrentDelegatedSession<Role>(await getBaseRESTURL(), user.access_token);
    const delegation = toUserDelegation(delegatedSession);
    syncDelegationScopedLocation(delegatedSession, oidcRole);

    set(state => {
      const name = (() => {
        const names = user.profile.name as unknown;
        if (typeof names === 'string') return names;
        return (names as string[]).toString().split(',')[1];
      })();
      return {
        ...state,
        user: { ...user, delegation } as User,
        delegatedSession,
        name,
        state: AuthenticationState.Authenticated,
        originalRole: oidcRole,
        isDelegatedSuperAdmin: delegatedSession?.effectiveRole === Role.Admin && oidcRole === Role.SuperAdmin,
        role: delegatedSession?.effectiveRole ?? oidcRole
      };
    });
    delegationDebug.log('restore', {
      originalRole: oidcRole,
      effectiveRole: delegatedSession?.effectiveRole ?? oidcRole,
      delegation: toUserDelegation(delegatedSession),
      delegatedSession
    });
    syncDelegationRenewal();
  },
  setUser: async (user: User) => {
    if (user.profile.organisation) await getTenantConfiguration(user.profile.organisation);

    const originalRole = user.profile[ROLE_KEY] as Role;
    syncDelegationScopedLocation(get().delegatedSession, originalRole);

    set(state => {
      const delegatedSession = state.delegatedSession;
      const role = delegatedSession?.effectiveRole ?? (user.profile[ROLE_KEY] as Role);
      const name = (() => {
        const names = user.profile.name as unknown;
        if (typeof names === 'string') return names;
        return (names as string[]).toString().split(',')[1];
      })();
      return {
        ...state,
        user: { ...user, delegation: toUserDelegation(delegatedSession) } as User,
        delegatedSession,
        state: AuthenticationState.Authenticated,
        originalRole,
        role,
        isDelegatedSuperAdmin: delegatedSession?.effectiveRole === Role.Admin && originalRole === Role.SuperAdmin,
        name
      };
    });
    delegationDebug.log('setUser', {
      originalRole,
      effectiveRole: get().delegatedSession?.effectiveRole ?? originalRole,
      delegation: get().user?.delegation,
      delegatedSession: get().delegatedSession
    });
    syncDelegationRenewal();
  },
  clearUser: async () => {
    useAdminLocationStore.getState().setActingLocation(undefined);
    set(state => {
      state.userManager.then(userManager => {
        userManager.clearStaleState();
      });
      return { ...state, user: undefined, delegatedSession: undefined, state: AuthenticationState.Unauthenticated };
    });
    syncDelegationRenewal();
  },
  userManager: (async () => {
    const { IDENTITY_CONFIG, METADATA_OIDC } = await getConfig();
    return new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      metadata: { ...METADATA_OIDC }
    });
  })(),
  startDelegation: async (targetTenantId: string, targetTenantName: string, delegatedRole: Role) => {
    const { user, originalRole } = get();
    if (!user?.access_token) {
      throw new Error('Missing access token');
    }

    const baseUrl = await getBaseRESTURL();
    const delegatedSession = (await startDelegatedSessionRequest<Role>(
      baseUrl,
      user.access_token,
      {
        targetTenantId,
        effectiveRole: delegatedRole
      },
      targetTenantName
    )) as DelegatedSession;

    set(state => ({
      ...state,
      delegatedSession,
      user: state.user ? ({ ...state.user, delegation: toUserDelegation(delegatedSession) } as User) : state.user,
      role: delegatedSession.effectiveRole,
      isDelegatedSuperAdmin: originalRole === Role.SuperAdmin && delegatedSession.effectiveRole === Role.Admin
    }));
    syncDelegationScopedLocation(delegatedSession, originalRole);
    delegationDebug.log('start', {
      targetTenantId,
      targetTenantName,
      delegatedRole,
      delegatedSession
    });
    syncDelegationRenewal();
  },
  endDelegation: async () => {
    const { user, originalRole } = get();
    if (!user?.access_token) {
      return;
    }

    const baseUrl = await getBaseRESTURL();
    const result = await endDelegatedSessionRequest(
      baseUrl,
      user.access_token,
      get().delegatedSession?.delegationSessionId
    );

    set(state => ({
      ...state,
      delegatedSession: undefined,
      user: state.user ? ({ ...state.user, delegation: toUserDelegation(undefined) } as User) : state.user,
      role: originalRole,
      isDelegatedSuperAdmin: false
    }));
    useAdminLocationStore.getState().setActingLocation(undefined);
    delegationDebug.log('end', {
      restoredRole: originalRole,
      status: result.status
    });
    syncDelegationRenewal();
  },
  getEffectiveTenantId: () => {
    const { user, delegatedSession, originalRole } = get();
    if (!user) return undefined;

    if (shouldUseDelegatedTenantId(delegatedSession, originalRole) && delegatedSession?.targetTenantId) {
      return delegatedSession.targetTenantId;
    }

    return user.profile?.organisation;
  }
}));

const authService = useAuthenticationService.getState();
void authService.restore().catch(error => {
  useAuthenticationService.setState(state => ({
    ...state,
    state: AuthenticationState.Unauthenticated
  }));
  throw error;
});

authService.userManager.then(userManager => {
  const renewAccessToken = async () => {
    try {
      const user = (await userManager.signinSilent()) as User;
      await authService.setUser(user);
    } catch {
      userManager.signinRedirect();
    }
  };

  userManager.events.addAccessTokenExpired(renewAccessToken);

  // In the scenario where the application is in the background (different active tab, device is sleeping, etc) the access token can become expired causing gql errors.
  // Previously we were only relying on "addAccessTokenExpired" to trigger in these scenarios, in order to renew the token, however, unfortunately it doesnt seem to always work.
  // It seems like "addAccessTokenExpiring" does, however, appear to get called in the background.
  // So a workaround to the issue is to renew here. (Note: this should result in the "addAccessTokenExpired" listener never getting fired.)
  userManager.events.addAccessTokenExpiring(renewAccessToken);
});

const renewDelegatedSession = async () => {
  const { user, originalRole } = useAuthenticationService.getState();
  const delegatedSessionId = useAuthenticationService.getState().delegatedSession?.delegationSessionId;
  if (!user?.access_token || !delegatedSessionId) {
    return;
  }

  const baseUrl = await getBaseRESTURL();
  const result = await renewDelegatedSessionRequest<Role>(baseUrl, user.access_token, delegatedSessionId);
  if (result.status === 'missing') {
    clearDelegationState();
    return;
  }

  const delegatedSession = result.session as DelegatedSession;
  useAuthenticationService.setState(current => ({
    ...current,
    delegatedSession,
    user: current.user ? ({ ...current.user, delegation: toUserDelegation(delegatedSession) } as User) : current.user,
    role: delegatedSession.effectiveRole,
    isDelegatedSuperAdmin: originalRole === Role.SuperAdmin && delegatedSession.effectiveRole === Role.Admin
  }));
  syncDelegationScopedLocation(delegatedSession, originalRole);
  syncDelegationRenewal();
};

const syncDelegationRenewal = () => {
  delegationRenewalController.sync();
};

export const getBaseRESTURL = async () => {
  const url = await getVariable('apiUrl');
  if (!url) {
    throw new Error('url is not defined.');
  }

  return url.replace('/graphql', '/api');
};

export const getAuthenticatedHeaders = (options?: { includeContentType?: boolean; includeAccept?: boolean }) => {
  const headers = getAuthorizationHeaders(options);
  const { getEffectiveTenantId } = useAuthenticationService.getState();
  const tenantId = getEffectiveTenantId();
  if (tenantId) headers['tenant-id'] = tenantId;
  const delegatedSessionId = useAuthenticationService.getState().delegatedSession?.delegationSessionId;
  if (delegatedSessionId) headers['x-delegation-session-id'] = delegatedSessionId;
  return headers;
};

export const getAuthorizationHeaders = (options?: { includeContentType?: boolean; includeAccept?: boolean }) => {
  const { user } = useAuthenticationService.getState();
  const headers: Record<string, string> = {};
  if (options?.includeContentType !== false) headers['Content-Type'] = 'application/json';
  if (options?.includeAccept !== false) headers.accept = 'application/json';
  if (user?.access_token) headers.Authorization = `Bearer ${user.access_token}`;
  return headers;
};

export const clearDelegationState = () => {
  const state = useAuthenticationService.getState();
  const originalRole = state.originalRole;
  // Acting location is delegation-scoped for Admin -> Manager and must not leak into the next session.
  useAdminLocationStore.getState().setActingLocation(undefined);

  useAuthenticationService.setState(current => ({
    ...current,
    delegatedSession: undefined,
    user: current.user ? ({ ...current.user, delegation: toUserDelegation(undefined) } as User) : current.user,
    role: originalRole,
    isDelegatedSuperAdmin: false
  }));
  delegationDebug.log('clear', {
    restoredRole: originalRole
  });
  syncDelegationRenewal();
};

export const handleDelegationTimeout = async (response: Response) => {
  if (response.status !== 403) {
    return response;
  }

  const { delegatedSession, user } = useAuthenticationService.getState();
  if (!delegatedSession || !user?.access_token) {
    return response;
  }

  // A 403 is not sufficient evidence that the delegated session expired; re-check the session before clearing UI state.
  const baseUrl = await getBaseRESTURL();
  const currentSession = await loadCurrentDelegatedSession<Role>(baseUrl, user.access_token);
  if (!currentSession) {
    clearDelegationState();
    return response;
  }

  useAuthenticationService.setState(current => ({
    ...current,
    delegatedSession: currentSession as DelegatedSession,
    user: current.user ? ({ ...current.user, delegation: toUserDelegation(currentSession) } as User) : current.user,
    role: currentSession.effectiveRole,
    isDelegatedSuperAdmin: current.originalRole === Role.SuperAdmin && currentSession.effectiveRole === Role.Admin
  }));
  syncDelegationRenewal();
  return response;
};

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') {
    return;
  }

  const delegatedSession = useAuthenticationService.getState().delegatedSession;
  if (!delegatedSession?.expiresAt) {
    return;
  }

  const expiresAtMs = new Date(delegatedSession.expiresAt).getTime();
  if (expiresAtMs - Date.now() <= DELEGATION_RENEW_LEAD_MS) {
    void renewDelegatedSession().catch(() => clearDelegationState());
    return;
  }

  syncDelegationRenewal();
});

delegationDebug.register(getDelegationDebugState);

export const useIsAdmin = () => {
  const [role] = useAuthenticationService(state => [state.role]);
  return isAdmin(role);
};

export const useIsManagement = () => {
  const [role] = useAuthenticationService(state => [state.role]);
  return isManagement(role);
};
